import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  TestTube, 
  CheckCircle, 
  AlertCircle,
  Server,
  Clock,
  Globe,
  Key,
  Activity,
  Zap,
  Shield,
  Database,
  Monitor
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const IntegrationManagement = () => {
  const [activeTab, setActiveTab] = useState('apis');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [integrations, setIntegrations] = useState({});
  const [apiKeys, setApiKeys] = useState({});
  const [webhooks, setWebhooks] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadIntegrationData();
  }, []);

  const loadIntegrationData = async () => {
    setLoading(true);
    try {
      const [integrationsRes, apiKeysRes, webhooksRes] = await Promise.all([
        MockDataService.getIntegrations(),
        MockDataService.getAPIKeys(),
        MockDataService.getWebhooks()
      ]);
      
      setIntegrations(integrationsRes.data);
      setApiKeys(apiKeysRes.data);
      setWebhooks(webhooksRes.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load integration data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving integration settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      localStorage.setItem('integrationSettings', JSON.stringify(integrations));
      localStorage.setItem('apiKeySettings', JSON.stringify(apiKeys));
      localStorage.setItem('webhookSettings', JSON.stringify(webhooks));
      
      setMessage({ type: 'success', text: 'Integration settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save integration settings' });
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (integrationType) => {
    try {
      // Simulate connection testing
      await new Promise(resolve => setTimeout(resolve, 2000));
      setMessage({ type: 'success', text: `${integrationType} connection test successful` });
    } catch (error) {
      setMessage({ type: 'error', text: `${integrationType} connection test failed` });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Integration Management</h1>
          <p className="text-gray-600 mt-2">Manage third-party integrations and API connections</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadIntegrationData}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {message && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('apis')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'apis'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Server className="h-4 w-4 inline mr-2" />
              API Integrations
            </button>
            <button
              onClick={() => setActiveTab('webhooks')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'webhooks'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Zap className="h-4 w-4 inline mr-2" />
              Webhooks
            </button>
            <button
              onClick={() => setActiveTab('keys')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'keys'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Key className="h-4 w-4 inline mr-2" />
              API Keys
            </button>
            <button
              onClick={() => setActiveTab('monitoring')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'monitoring'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Monitor className="h-4 w-4 inline mr-2" />
              Monitoring
            </button>
          </nav>
        </div>

        {activeTab === 'apis' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Third-Party API Integrations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="googleApi">Google API Integration</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="googleApi"
                        checked={integrations.googleApi || false}
                        onChange={(e) => setIntegrations({...integrations, googleApi: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Enable Google Workspace integration</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="microsoftApi">Microsoft API Integration</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="microsoftApi"
                        checked={integrations.microsoftApi || false}
                        onChange={(e) => setIntegrations({...integrations, microsoftApi: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Enable Microsoft 365 integration</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salesforceApi">Salesforce Integration</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="salesforceApi"
                        checked={integrations.salesforceApi || false}
                        onChange={(e) => setIntegrations({...integrations, salesforceApi: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Enable Salesforce CRM integration</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slackApi">Slack Integration</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="slackApi"
                        checked={integrations.slackApi || false}
                        onChange={(e) => setIntegrations({...integrations, slackApi: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-600">Enable Slack notifications</span>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold mb-4">API Configuration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="apiTimeout">API Timeout (seconds)</Label>
                      <Input
                        id="apiTimeout"
                        type="number"
                        value={integrations.apiTimeout || 30}
                        onChange={(e) => setIntegrations({...integrations, apiTimeout: parseInt(e.target.value)})}
                        min="5"
                        max="300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="retryAttempts">Retry Attempts</Label>
                      <Input
                        id="retryAttempts"
                        type="number"
                        value={integrations.retryAttempts || 3}
                        onChange={(e) => setIntegrations({...integrations, retryAttempts: parseInt(e.target.value)})}
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'webhooks' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Webhook Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableWebhooks">Enable Webhooks</Label>
                      <p className="text-sm text-gray-600">Allow external systems to receive notifications</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableWebhooks"
                      checked={webhooks.enabled || false}
                      onChange={(e) => setWebhooks({...webhooks, enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="webhookUrl">Default Webhook URL</Label>
                    <Input
                      id="webhookUrl"
                      value={webhooks.defaultUrl || ''}
                      onChange={(e) => setWebhooks({...webhooks, defaultUrl: e.target.value})}
                      placeholder="https://example.com/webhook"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="webhookSecret">Webhook Secret</Label>
                    <Input
                      id="webhookSecret"
                      type="password"
                      value={webhooks.secret || ''}
                      onChange={(e) => setWebhooks({...webhooks, secret: e.target.value})}
                      placeholder="Enter webhook secret for security"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="webhookTimeout">Timeout (seconds)</Label>
                      <Input
                        id="webhookTimeout"
                        type="number"
                        value={webhooks.timeout || 10}
                        onChange={(e) => setWebhooks({...webhooks, timeout: parseInt(e.target.value)})}
                        min="1"
                        max="60"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="webhookRetries">Retry Attempts</Label>
                      <Input
                        id="webhookRetries"
                        type="number"
                        value={webhooks.retries || 3}
                        onChange={(e) => setWebhooks({...webhooks, retries: parseInt(e.target.value)})}
                        min="0"
                        max="10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'keys' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  API Key Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableApiKeys">Enable API Key Authentication</Label>
                      <p className="text-sm text-gray-600">Allow API access using key-based authentication</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableApiKeys"
                      checked={apiKeys.enabled || false}
                      onChange={(e) => setApiKeys({...apiKeys, enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="keyLength">API Key Length</Label>
                    <Select
                      value={apiKeys.keyLength || '32'}
                      onValueChange={(value) => setApiKeys({...apiKeys, keyLength: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="16">16 characters</SelectItem>
                        <SelectItem value="32">32 characters</SelectItem>
                        <SelectItem value="64">64 characters</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="keyExpiry">Key Expiry (days)</Label>
                    <Input
                      id="keyExpiry"
                      type="number"
                      value={apiKeys.expiryDays || 365}
                      onChange={(e) => setApiKeys({...apiKeys, expiryDays: parseInt(e.target.value)})}
                      min="1"
                      max="3650"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="rateLimit">Rate Limit (requests per minute)</Label>
                    <Input
                      id="rateLimit"
                      type="number"
                      value={apiKeys.rateLimit || 1000}
                      onChange={(e) => setApiKeys({...apiKeys, rateLimit: parseInt(e.target.value)})}
                      min="1"
                      max="10000"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="h-5 w-5" />
                  Integration Monitoring
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="enableMonitoring">Enable Integration Monitoring</Label>
                      <p className="text-sm text-gray-600">Monitor integration health and performance</p>
                    </div>
                    <input
                      type="checkbox"
                      id="enableMonitoring"
                      checked={integrations.monitoringEnabled || false}
                      onChange={(e) => setIntegrations({...integrations, monitoringEnabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="monitoringInterval">Monitoring Interval (minutes)</Label>
                    <Input
                      id="monitoringInterval"
                      type="number"
                      value={integrations.monitoringInterval || 5}
                      onChange={(e) => setIntegrations({...integrations, monitoringInterval: parseInt(e.target.value)})}
                      min="1"
                      max="60"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="alertThreshold">Alert Threshold (failures)</Label>
                    <Input
                      id="alertThreshold"
                      type="number"
                      value={integrations.alertThreshold || 5}
                      onChange={(e) => setIntegrations({...integrations, alertThreshold: parseInt(e.target.value)})}
                      min="1"
                      max="100"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notificationEmail">Notification Email</Label>
                    <Input
                      id="notificationEmail"
                      type="email"
                      value={integrations.notificationEmail || ''}
                      onChange={(e) => setIntegrations({...integrations, notificationEmail: e.target.value})}
                      placeholder="admin@company.com"
                    />
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-lg font-semibold mb-4">Integration Status</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Google API</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Microsoft API</span>
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Salesforce</span>
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Error
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium">Slack</span>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationManagement;
